import { Button, Form, Input, Select, Space, Typography } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { useReferentialAssetTypeCategories } from "@/entities/referential/referential-asset-type-categories";
import { useCreate, useUpdate } from "@/hooks/queries";
import AssetTypeTemplateSelect from "@/modules/asset-type-template/asset-type-template-select";
import { validationErrorParser } from "@/modules/form-validation";
import { getModelLink } from "@/modules/model";
import { getModelSelectOptionValue } from "@/modules/select";
import { SidePanelFooter, useSidePanel } from "@/modules/side-panel";
import { Alert } from "@/ui/alert";
import { FormItemAlert } from "@/ui/form-item-alert";

const { useForm } = Form;

const AssetTypeForm = ({ model = null }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeSidePanel } = useSidePanel();

  const { assetTypeCategories } = useReferentialAssetTypeCategories();
  const assetTypeCategoriesOptions = assetTypeCategories.map((category) => {
    return { value: category.slug, label: category.name };
  });

  const mutationUpdate = useUpdate("assetTypes", model?.id);
  const mutateCreate = useCreate("assetTypes");
  const mutation = model ? mutationUpdate : mutateCreate;
  const isNew = model === null;

  const initialValues = {
    name: model?.name,
    asset_type_category: model?.asset_type_category,
    entityTemplate: getModelSelectOptionValue(model?.entity_template),
  };

  const onClose = () => {
    form.resetFields();
    closeSidePanel();
  };

  const validationErrors = () => {
    return mutation.isError
      ? validationErrorParser(
          mutation.error,
          ["name", "asset_type_category", "entity_template_id"],
          "others"
        )
      : {};
  };

  const validation = (item) => {
    return {
      validateStatus: validationErrors()[item] ? "error" : "status",
      help: validationErrors()[item],
    };
  };

  const onSubmit = (values) => {
    const data = {
      name: values.name,
      asset_type_category: values.asset_type_category,
      entity_template_id: values.entityTemplate?.value ?? null,
    };
    mutation.mutate(data, {
      onSuccess: ({ data }) => {
        onClose();
        if (isNew) {
          const newAssetType = data?.data;
          const link = getModelLink("assettype", newAssetType);
          const search = `?${TAB_PARAM_KEYS.CONTEXT_TAB_KEY}=${TAB_KEYS.TEMPLATE_FORM_EDITOR}`;
          navigate(`${link}${search}`);
        }
      },
    });
  };

  return (
    <>
      <Form
        id="assetTypeForm"
        form={form}
        layout="vertical"
        requiredMark
        initialValues={initialValues}
        onFinish={onSubmit}
        role="form"
      >
        <Typography.Title level={3}>
          {t("assetType:information.label")}
        </Typography.Title>
        <Form.Item
          name="name"
          label={t("assetType:form.name.label")}
          rules={[{ required: true }]}
          {...validation("name")}
        >
          <Input placeholder={t("assetType:form.name.label")} />
        </Form.Item>
        <Form.Item
          name="asset_type_category"
          label={t("assetType:form.category.label")}
          {...validation("asset_type_category")}
        >
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder={t("assetType:form.category.label")}
            options={assetTypeCategoriesOptions}
          />
        </Form.Item>
        <AssetTypeTemplateSelect {...validation("entity_template_id")} />
        {!isNew && (
          <FormItemAlert title={t("assetType:form.technicalForm.alert")} />
        )}
        {validationErrors().others && <Form.Item {...validation("others")} />}
        <Alert
          display={mutation.isError}
          closable={{ afterClose: () => mutation.reset() }}
        />
        <SidePanelFooter>
          <Space>
            <Button onClick={onClose}>{t("global:cancel")}</Button>
            <Button
              loading={mutation.isPending}
              type="primary"
              htmlType="submit"
              form="assetTypeForm"
            >
              {isNew ? t("global:add") : t("global:update")}
            </Button>
          </Space>
        </SidePanelFooter>
      </Form>
    </>
  );
};

AssetTypeForm.propTypes = {
  model: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};

export default AssetTypeForm;
